import {
  Col,
  Button,
  Table,
  Empty,
  Input,
  message,
  Card,
  theme,
  Tooltip,
  Typography,
  Divider,
  Row,
  Space,
  Badge,
  Radio,
  Skeleton
} from 'antd'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faRefresh,
  faSearch,
  faNotesMedical,
  faPrescriptionBottle,
  faCalendarAlt,
  faPlus,
  faFilter
} from '@fortawesome/free-solid-svg-icons'

import { ScriptHistoryTableTemplate } from '../utils/templates'
import CustomRow from '../../reusable-components/CustomRow'
import { useCallback, useEffect, useState } from 'react'
import { readPatients } from '../../Admin/patients/utils/utils'
import { handleError } from '../../lib/utils'
import { readScriptHistory, readScripts, removeScript } from '../utils/utils'
import dayjs from 'dayjs'
import debounce from 'lodash/debounce'
import CustomLoadingIcon from '../../reusable-components/CustomLoadingIcon'
import '../styles/scripting-history-list.css'
import PatientSearchModal from './PatientSearchModal'
import ScriptingTableControls from './ScriptingTableControls'
import { ContainerCard } from '../../reusable-components/AgiliteCards'
import PageHeader from '../../reusable-components/PageHeader'

const { Title, Text } = Typography

// Add spin animation with more refined keyframes
const spinAnimation = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    50% { transform: rotate(180deg); }
    100% { transform: rotate(360deg); }
  }
  
  .ant-table-row:hover .service-icon {
    transform: scale(1.1);
    transition: all 0.2s ease;
  }
  
  .service-icon {
    transition: all 0.2s ease;
  }
  
  .ant-table-row {
    transition: all 0.3s ease;
  }
  
  .ant-table-row:hover {
    background-color: rgba(24, 144, 255, 0.05) !important;
  }

  .ant-table-thead th h2 {
    font-size: 15px !important;
    margin: 0;
  }
`

const ScriptHistoryList = ({
  data,
  patientId,
  refreshView,
  createNewScript,
  viewScript,
  loading,
  setLoading,
  handleSearch,
  patientSearchQuery,
  handleGenerateScriptTemplate,
  printingScript,
  total,
  setPageSize,
  setPage,
  page,
  pageSize,
  type,
  setType,
  onlyDue,
  setOnlyDue,
  totalDue,
  setCurrentScript,
  recitfyOldRecord
}) => {
  const [modalOpen, setModalOpen] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const { token } = theme.useToken()

  useEffect(() => {
    handlePatientSearch()
    // eslint-disable-next-line
  }, [searchQuery])

  const handlePatientSearch = async () => {
    let tmpUsers = []
    try {
      setSearching(true)
      if (searchQuery) {
        const qry = {
          'extraData.role.type': 'patient',
          $or: [
            {
              $expr: {
                $regexMatch: {
                  input: { $concat: ['$firstName', ' ', '$lastName'] },
                  regex: searchQuery,
                  options: 'i'
                }
              }
            },
            { phoneNumber: { $regex: searchQuery, $options: 'i' } },
            { email: { $regex: searchQuery, $options: 'i' } },
            { physicalAddress: { $regex: searchQuery, $options: 'i' } },
            { idNo: { $regex: searchQuery, $options: 'i' } }
          ]
        }
        tmpUsers = await readPatients(qry)
      }
      setSearchResults(tmpUsers)
    } catch (e) {
      message.error(handleError(e))
    } finally {
      setSearching(false)
    }
  }

  const handlePatientQuery = (query) => {
    setSearching(true)
    debouncedFilter(query)
  }

  // eslint-disable-next-line
  const debouncedFilter = useCallback(
    debounce((query) => {
      setSearchQuery(query)
    }, 1000),
    []
  )

  const handleRemoveScript = async (recordId) => {
    setLoading(true)
    try {
      await removeScript(recordId)
      message.success('Removed Script')
      refreshView()
    } catch (e) {
      message.error(handleError(e))
      setLoading(false)
    }
  }

  const filterByDue = () => {
    const todaysDate = dayjs().format('YYYY-MM-DD')
    return data.filter((entry) => {
      const nextDueDate = dayjs(entry.nextDue).format('YYYY-MM-DD')
      if (nextDueDate === todaysDate) return true
      if (todaysDate > nextDueDate) {
        entry.daysOverDue = dayjs(todaysDate).diff(nextDueDate, 'day')
        return true
      }
      return false
    })
  }

  const CustomEmptyState = () => (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div style={{ textAlign: 'center' }}>
          <h3 style={{ color: token.colorTextSecondary, marginBottom: 8 }}>
            {type === 'schedule' ? 'No Due Scripts' : 'No Scripts Found'}
          </h3>
        </div>
      }
    />
  )

  const handleCheckDocument = async (record, isDownload) => {
    const targetId = record._id
    try {
      const targetDocument = await readScripts({ _id: targetId }, 'document')
      if (targetDocument[0].document) {
        const base64Data = targetDocument[0].document

        if (isDownload) {
          // Create download link using the data URL
          const link = document.createElement('a')
          link.download = `script_${targetId}.pdf`
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
        } else {
          setCurrentScript(base64Data)
        }
      } else {
        recitfyOldRecord(record)
      }
    } catch (error) {
      console.error('Document error:', error)
      message.error('Unable to view document.')
    }
  }

  // Generate skeleton rows for loading state
  const generateSkeletonRows = () => {
    return Array(5)
      .fill()
      .map((_, i) => ({
        key: `skeleton-${i}`,
        _id: `skeleton-${i}`,
        skeleton: true
      }))
  }

  // Generate skeleton columns for loading state
  const skeletonColumns = () => {
    // Get columns and filter out action columns as needed
    const columns = ScriptHistoryTableTemplate(
      patientId ? true : false,
      handleGenerateScriptTemplate,
      printingScript,
      handleRemoveScript,
      type === 'schedule',
      viewScript,
      setCurrentScript,
      recitfyOldRecord,
      handleCheckDocument
    )
      .filter((i) => !i.hidden)
      .map((col) => ({
        ...col,
        render: () => <Skeleton.Input active size='small' style={{ width: '100%' }} />,
        onCell: () => ({
          style: { cursor: 'default' }
        })
      }))

    return columns
  }

  return (
    <>
      <style>{spinAnimation}</style>
      {/* Header Section */}
      <PageHeader title='Prescriptions Management' icon={faNotesMedical} />

      <div style={{ padding: '16px 24px' }}>
        {/* Search & Filters */}
        <div>
          <Row align='middle' justify='space-between' wrap>
            <Col>
              <Space wrap>
                {!patientId ? (
                  <Input
                    placeholder='Search patients by name or ID...'
                    defaultValue={patientSearchQuery}
                    allowClear
                    onChange={(e) => handleSearch(e.target.value)}
                    disabled={loading}
                    prefix={
                      <div
                        style={{
                          background: token.actionColor || token.colorPrimary,
                          borderRadius: '50%',
                          width: '24px',
                          height: '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: '8px'
                        }}
                      >
                        <FontAwesomeIcon icon={faSearch} style={{ color: 'white', fontSize: '12px' }} />
                      </div>
                    }
                    style={{
                      width: '300px',
                      maxWidth: '100%',
                      borderRadius: '6px',
                      height: '40px'
                    }}
                    size='middle'
                  />
                ) : undefined}
                {/* 
                  <Button
                    style={{
                      ...buttonStyle,
                      background: token.actionColor || token.colorPrimary,
                      borderColor: token.actionColor || token.colorPrimary,
                      color: '#ffffff'
                    }}
                    onClick={handleSetToday}
                  >
                    Today
                  </Button> */}

                <Radio.Group value={type} onChange={(e) => setType(e.target.value)} buttonStyle='solid'>
                  <Tooltip title='All Scripts'>
                    <Radio.Button
                      value=''
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '6px 16px',
                        borderRadius: '6px 0 0 6px'
                      }}
                    >
                      <FontAwesomeIcon icon={faPrescriptionBottle} />
                      <span style={{ marginLeft: 8 }}>All</span>
                    </Radio.Button>
                  </Tooltip>
                  <Tooltip title='Scheduled Scripts'>
                    <Radio.Button
                      value='schedule'
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '6px 16px',
                        borderRadius: '0 6px 6px 0'
                      }}
                    >
                      <FontAwesomeIcon icon={faCalendarAlt} />
                      <span style={{ marginLeft: 8 }}>Scheduled</span>
                      {totalDue > 0 && <Badge count={totalDue} style={{ marginLeft: 8 }} />}
                    </Radio.Button>
                  </Tooltip>
                </Radio.Group>
              </Space>
            </Col>
            <Col>
              <Row gutter={[8, 8]} justify='end'>
                <Col>
                  <Button
                    icon={
                      <FontAwesomeIcon
                        icon={faRefresh}
                        style={{
                          color: token.actionColor || token.colorPrimary,
                          animation: loading ? 'spin 1s linear infinite' : 'none',
                          marginRight: 8
                        }}
                      />
                    }
                    onClick={refreshView}
                    disabled={loading}
                    style={{
                      background: 'rgba(24, 144, 255, 0.1)',
                      borderColor: 'rgba(24, 144, 255, 0.3)',
                      color: token.actionColor || token.colorPrimary
                    }}
                  >
                    Refresh
                  </Button>
                </Col>
                <Col>
                  <Button
                    icon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => (patientId ? createNewScript(patientId) : setModalOpen(true))}
                    style={{
                      background: token.actionColor || token.colorPrimary,
                      borderColor: token.actionColor || token.colorPrimary,
                      color: '#ffffff'
                    }}
                  >
                    New Script
                  </Button>
                </Col>
              </Row>
            </Col>
          </Row>

          <div style={{ marginBottom: 16 }}>
            <ScriptingTableControls type={type} onlyDue={onlyDue} setOnlyDue={setOnlyDue} token={token} />
          </div>
        </div>

        {/* Table */}
        <Table
          bordered
          className={'scriptHistoryTable'}
          loading={false} // We handle loading state with skeletons
          size='small'
          pagination={{
            current: page,
            pageSize: pageSize,
            total: total,
            onChange: (p, ps) => {
              setPage(p)
              setPageSize(ps)
            },
            showSizeChanger: true,
            showTotal: (total) => 'Total ' + total + ' scripts',
            position: ['bottomCenter'],
            pageSizeOptions: ['10', '20', '50', '100'],
            style: { marginTop: 16 }
          }}
          rowKey={(record) => record._id}
          dataSource={loading ? generateSkeletonRows() : onlyDue && type === 'schedule' ? filterByDue() : data}
          scroll={{ x: 950 }}
          columns={
            loading
              ? skeletonColumns()
              : ScriptHistoryTableTemplate(
                  patientId ? true : false,
                  handleGenerateScriptTemplate,
                  printingScript,
                  handleRemoveScript,
                  type === 'schedule',
                  viewScript,
                  setCurrentScript,
                  recitfyOldRecord,
                  handleCheckDocument
                ).filter((i) => !i.hidden)
          }
          locale={{
            emptyText: loading ? (
              <div style={{ padding: '40px 0' }}>
                <Skeleton active paragraph={{ rows: 3 }} />
              </div>
            ) : (
              <CustomEmptyState />
            )
          }}
          style={{
            borderRadius: 8,
            overflow: 'hidden',
            position: 'relative',
            minHeight: '400px'
          }}
          rowClassName={(record) => (record.skeleton ? 'skeleton-row' : '')}
        />
      </div>

      <PatientSearchModal
        visible={modalOpen}
        onCancel={() => {
          setModalOpen(false)
          setSearchQuery('')
        }}
        onSearch={handlePatientQuery}
        loading={searching}
        searchResults={searchResults}
        onSelectPatient={(record) => {
          setModalOpen(false)
          setSearchQuery('')
          createNewScript(record)
        }}
      />
    </>
  )
}

export default ScriptHistoryList
